import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Loader } from '@googlemaps/js-api-loader'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import {
    AssetType,
    BucketDataType,
    LocationHistoryType,
    SelectedPingType,
    TagType,
} from '../../types/bucketTypes'
import { getImgSrc } from '../../helpers/getImageSrc'
import { ASSET_DATA } from '../../constants/bucket.contants'
import { darkModeStyle } from '../../constants/navigation.constants'
import { useDarkMode } from '../../hooks/use-darkmode'

interface Props {
    selectedAsset?: any
    bucketList?: AssetType[]
    setIsModalShown?: Dispatch<SetStateAction<boolean>>
    isModalShown?: boolean
    setSelectedAsset?: Dispatch<SetStateAction<BucketDataType>>
    tagData?: TagType
    isLocationHistory?: boolean
    locationHistory?: LocationHistoryType[]
    selectedPing?: SelectedPingType
    getTagData?: any
    setTagData?: Dispatch<any>
    zoom?: number
}

export function DisplayMap(props: Readonly<Props>) {
    const {
        bucketList,
        setIsModalShown,
        setSelectedAsset,
        tagData,
        isLocationHistory,
        locationHistory,
        selectedPing,
        isModalShown,
        getTagData,
        setTagData
    } = props;

    const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
    const loader = new Loader({
        apiKey: process.env.REACT_APP_MAPS_API_KEY as string,
    });

    const requestGeolocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            console.warn("User denied the request for Geolocation.");
                            break;
                        case error.POSITION_UNAVAILABLE:
                            console.warn("Location information is unavailable.");
                            break;
                        case error.TIMEOUT:
                            console.warn("The request to get user location timed out.");
                            break;
                        default:
                            console.warn("An unknown error occurred.");
                            break;
                    }
                    // Fallback to a default location if geolocation fails
                    setUserLocation({
                        lat: 38.7946,
                        lng: -95.7129,
                      });
                },
                {
                    enableHighAccuracy: false, // Request approximate location
                    timeout: 5000, // 5 seconds timeout
                    maximumAge: 0,
                }
            );
        } else{
            setUserLocation({
                lat: 38.7946,
                lng: -95.7129,
              });
        }
    };
    useEffect(() => {
        requestGeolocation();
    }, []);

    const {darkMode} = useDarkMode()

    const handleMarkerClick = (buckets: AssetType) => {
        sessionStorage.setItem(ASSET_DATA, JSON.stringify(buckets));
        if (setSelectedAsset && setIsModalShown) {
            if (getTagData && setTagData) {
                let tagInfo = getTagData(buckets);
                setTagData?.(tagInfo);
            }
            setSelectedAsset(buckets.bucket);
            setIsModalShown(true);
        }
    };

    const getMapCenter = (assets?: any) => {
        let center = {
            lat: 37.0902,
            lng: -95.7129,
        };
        if (userLocation) {
            center = userLocation;
        } 
        if (tagData && assets) {
            if(locationHistory){
                center = {
                lat: assets[0].latitude,
                lng: assets[0].longitude,
            };
            }else{
                center = {
                    lat: assets[0].location?.latitude,
                    lng: assets[0].location?.longitude,
                };
            }
            
        } 
        if (selectedPing) {
            center = {
                lat: selectedPing.lat,
                lng: selectedPing.lng,
            };
        }
        return center;
    };

    const getAssets = () => {
        if (tagData) {
            if (locationHistory) return locationHistory;
            return [tagData];
        }
        if (bucketList) return bucketList;
        
        return [];
    };

    const getMapElementId = () => {
        if (tagData) {
            return isLocationHistory
                ? 'location-history-map'
                : 'locations-tab-map';
        }
        return 'map';
    };

    const initializeMap = (
        mapElementId: string,
        mapCenter: google.maps.LatLngLiteral,
        darkMode: boolean,
        zoom?: number
    ) => {
        return new google.maps.Map(
            document.getElementById(mapElementId) as HTMLElement,
            {
                center: mapCenter,
                styles: darkMode? darkModeStyle :[],
                zoom: props.zoom ?? zoom,
            }
        );
    };

    // Helper to get the correct bucket/location
    const getBucketLocation = (buckets: any) => {
        if (bucketList) {
            return buckets?.tag?.location;
        } else if (tagData) {
            if (locationHistory) {
                return buckets;
            } else {
                return buckets?.location;
            }
        }
        return undefined;
    };

    // Helper to build marker options
    const buildMarkerOptions = (bucket: any, buckets: any) => {
        const position = {
            lat: bucket?.latitude,
            lng: bucket?.longitude,
        };
        const isSelected =
            selectedPing &&
            bucket?.latitude === selectedPing.lat &&
            bucket?.longitude === selectedPing.lng;
        const markerOptions: google.maps.MarkerOptions = { position };
        if (!isSelected) {
            markerOptions.icon = {
                url: getImgSrc(bucketList ? buckets.tag : buckets, true),
                scaledSize: new google.maps.Size(25, 30),
            };
        }
        return markerOptions;
    };

    // Helper for marker click
    const markerClickHandler = (buckets: any) => {
        if (
            bucketList &&
            (!tagData || tagData === undefined || bucketList.length > 0)
        ) {
            handleMarkerClick(buckets);
        }
    };

    const addMarkersToMap = (map: google.maps.Map, assets: any[]) => {
        const markers = assets.map((buckets: any) => {
            const bucket = getBucketLocation(buckets);
            if (bucket?.latitude && bucket?.longitude) {
                const markerOptions = buildMarkerOptions(bucket, buckets);
                const marker = new google.maps.Marker(markerOptions);
                marker.addListener('click', () => markerClickHandler(buckets));
                return marker;
            }
            return undefined;
        }).filter((marker: any): marker is google.maps.Marker => marker !== undefined);

        const markerCluster = new MarkerClusterer({ map, markers });
        // This console purpose is for markercluster to be used, otherwise it will show error on sonarlint
        console.info('MarkerCluster', markerCluster);
    };

    const loadMap = () => {
        const assets = getAssets();
        const mapElementId = getMapElementId();
        const centerParams = tagData ? assets : null;
        const mapCenter = getMapCenter(centerParams);
        const zoom = 5
        loader.load().then(() => {
            const map = initializeMap(mapElementId, mapCenter, darkMode, zoom)
            if (assets.length > 0) {
                addMarkersToMap(map, assets);
            }
        });
    };

    useEffect(() => {
        loadMap();
    }, [locationHistory, selectedPing]);
    useEffect(()=>{ loadMap()},[darkMode])
    const renderMap = () => {
        if (tagData) {
            return (
                <div
                    id={
                        isLocationHistory
                            ? 'location-history-map'
                            : 'locations-tab-map'
                    }
                    data-testid={
                        isLocationHistory
                            ? 'location-history-map'
                            : 'locations-tab-map'
                    }
                    style={
                        isLocationHistory
                            ? { height: '100%', width: '100%' }
                            : { height: '300px', width: '100%' }
                    }
                />
            );
        } else {
            return (
                <div
                    className={isModalShown ? 'disable-pointer' : ''}
                    id="map"
                    style={{ height: '100%' }}
                />
            );
        }
    };

    return <>
    {renderMap()}
    </>;
}